import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { GlobalState } from 'reducers/stateTypes';
import LoadingIndicator from 'components/Common/LoadingIndicator';
import { useRuntimeConfig } from 'config/useRuntimeConfig';
import { usePostMessageToIFrame } from 'hooks/usePostMessageToIFrame';
import { ClmPortalOutgoingPostMessageTypes } from 'utils/postMessageUtil';
import { IframeComponent } from 'config/config.types';
import { isNullOrUndefined } from 'utils/utils';

/**
 * A fullscreen embedded iframe component.
 * @param component the name of the iframe.
 * This is used as a prefix of the url if set.
 * @param url The URL of the iFrame
 * @param title Title of the iFrame
 * @param id id of the iFrame
 * @param height height of the iFrame
 * @param centered alignment of the iFrame
 * @param loading
 * @param iframeRef ref to the rendered iframe
 */
const EmbeddedIFrame = ({
  component,
  url,
  title,
  id,
  height = '100%',
  centered = false,
  loading = false,
  iframeRef,
}: {
  component?: IframeComponent;
  url: string;
  title: string;
  id: string;
  height?: string;
  centered?: boolean;
  loading?: boolean;
  iframeRef?: React.RefObject<HTMLIFrameElement | null>;
}) => {
  const [loaded, setLoaded] = useState(false);
  const internalIFrameRef = useRef<HTMLIFrameElement>(null);
  const resolvedIFrameRef = iframeRef ?? internalIFrameRef;

  const { originUrls, isLoading: isLoadingRuntimeConfig, configData } = useRuntimeConfig();
  const { postMessageToIFrame } = usePostMessageToIFrame();

  const gravityEnabled: boolean = useSelector((state: GlobalState) => state.userFeedback.isTutorialEnabled);

  const builtUrl = isNullOrUndefined(component) ? url : configData.iframeUrls[component] + url;

  useEffect(() => {
    if (loaded && gravityEnabled) {
      const timer = setTimeout(() => {
        postMessageToIFrame(
          { message: ClmPortalOutgoingPostMessageTypes.INJECT_GRAVITY },
          resolvedIFrameRef.current ?? undefined,
        );
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [loaded, gravityEnabled]);

  if (loading || isLoadingRuntimeConfig) {
    return <LoadingIndicator id={`${id}-progressbar`} />;
  }

  return (
    <>
      {!loaded && <LoadingIndicator id={`${id}-progressbar`} />}
      <iframe
        ref={resolvedIFrameRef}
        style={{
          ...(!centered && { position: 'fixed', left: 0, right: 0 }),
          width: '100%',
          height: centered ? height : `calc((${height}) - var(--navbar-height, 0px))`,
          transition: 'height 0.2s',
        }}
        src={builtUrl}
        title={title}
        frameBorder="no"
        onLoad={() => {
          setLoaded(true);
        }}
        id={id}
        data-testid={id}
        allow={'clipboard-write ' + originUrls?.join(' ')}
        allowFullScreen
      />
    </>
  );
};

export default EmbeddedIFrame;
